import React from "react";
import rjStore from "../assets/portfolio/rjStore.png";
import rjJobs from "../assets/portfolio/rjJobs.png";
import rjPix from "../assets/portfolio/rjPix.png";

const Portfolio = () => {
  const portfolios = [
    {
      id: 1,
      name: "College Cosmos",
      type: "Product Engineer",
      desc: "Education counselling and admissions platform with product engineering, CMS, SEO, analytics, and lead-generation workflows.",
      impact: [
        "Implemented a Next.js site with Strapi CMS for blogs, landing pages, and admissions content",
        "Implemented Google Tag Manager and Meta Pixel, with 15.6K impressions and 131 clicks in a recent 28-day Search Console view",
      ],
      techStack: [
        "Next.js",
        "TypeScript",
        "shadcn/ui",
        "Tailwind CSS",
        "Strapi CMS",
      ],
      liveLink: "https://collegecosmos.in",
      featured: true,
    },
    {
      id: 2,
      name: "Glamnexx",
      type: "Product / Full Stack Engineer",
      desc: "SaaS product for appointment-led local service businesses, including salons, spas, tattoo studios, and beauty clinics.",
      impact: [
        "Building dashboard workflows for customer engagement, lead management, and business operations",
        "Integrating Meta WhatsApp APIs, webhooks, template creation, and AI assistant workflows",
        "Completed Meta Tech Provider access verification and built onboarding flows for business assets and WhatsApp templates",
      ],
      techStack: ["Next.js", "TypeScript", "Supabase", "Meta APIs", "Webhooks"],
      liveLink: "https://glamnexx.com",
      featured: true,
    },
    {
      id: 3,
      name: "Runa HR Payroll Workflows",
      type: "Client Product Engineering",
      desc: "Payroll management, configuration, and dashboard workflows for a YC-backed HRMS product.",
      impact: [
        "Developed frontend modules with Next.js, React, TypeScript, Material UI, React Query, and Jotai",
        "Integrated REST/gRPC-backed workflows through BFF layers, shared libraries, i18n, Elasticsearch features, and tests",
      ],
      techStack: [
        "React.js",
        "Next.js",
        "TypeScript",
        "Material UI",
        "React Query",
      ],
      featured: true,
    },
    {
      id: 4,
      name: "Vehicle Tracking CRM",
      type: "Frontend Engineering",
      desc: "Map-based CRM workflows for vehicle tracking operations.",
      impact: [
        "Implemented Leaflet features for geofencing and route drawing",
        "Built responsive pages, UI components, and fixes within a team codebase",
      ],
      techStack: ["React.js", "JavaScript", "Leaflet", "HTML", "CSS"],
    },
    {
      id: 5,
      src: rjStore,
      name: "RJ Store",
      type: "Frontend Project",
      desc: "Furniture e-commerce interface with catalog filtering by price, color, company, and category.",
      impact: [
        "Catalog browsing and filter interaction",
        "Responsive product-listing UI",
      ],
      techStack: ["React.js", "JavaScript", "Styled Components"],
      liveLink: "https://rj-furniture-store.netlify.app",
      codeLink: "https://github.com/Raj-Stark/Rj-Store",
    },
    {
      id: 6,
      src: rjJobs,
      name: "Jobster",
      type: "Frontend Project",
      desc: "Job application dashboard with CRUD workflows for creating, updating, and managing job records.",
      impact: ["Dashboard CRUD flow", "Redux-based frontend state management"],
      techStack: ["React.js", "JavaScript", "Redux", "Styled Components"],
      liveLink: "https://rj-jobs.netlify.app/landing",
      codeLink: "https://github.com/Raj-Stark/Job-Listing-Website",
    },
    {
      id: 7,
      src: rjPix,
      name: "RJ Pix",
      type: "Frontend Project",
      desc: "Image search application using the Unsplash API with search and infinite scrolling.",
      impact: ["API-based image search", "Tailwind CSS responsive UI"],
      techStack: ["React.js", "JavaScript", "Tailwind CSS", "API Integration"],
      liveLink: "https://rajpix.netlify.app/",
      codeLink: "https://github.com/Raj-Stark/Unsplash-API",
    },
  ];

  return (
    <section
      name="portfolio"
      className="bg-[#080b0d] px-5 py-24 text-white lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-emerald-300">
            Selected Work
          </p>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            Selected products and frontend work across SaaS, HRMS, automation,
            and API-driven UI.
          </h2>
          <p className="mt-5 text-lg leading-8 text-zinc-400">
            These projects reflect production workflows, live product
            ownership, API integrations, dashboard development, SEO, analytics,
            and frontend implementation.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {portfolios.map(
            ({
              id,
              src,
              name,
              type,
              desc,
              impact,
              techStack,
              liveLink,
              codeLink,
              featured,
            }) => (
              <article
                key={id}
                className={`flex h-full flex-col overflow-hidden rounded-md border bg-[#111719] shadow-lg shadow-black/20 transition hover:-translate-y-1 hover:shadow-2xl ${
                  featured ? "border-emerald-400/30" : "border-white/10"
                }`}
              >
                {src ? (
                  <img
                    src={src}
                    alt={name}
                    className="h-56 w-full border-b border-white/10 object-cover"
                  />
                ) : (
                  <div className="flex h-56 items-end border-b border-white/10 bg-[linear-gradient(135deg,_#111719_0%,_#065f46_55%,_#27272a_100%)] p-6">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-[0.16em] text-emerald-100">
                        {type}
                      </p>
                      <h3 className="mt-3 text-3xl font-extrabold text-white">
                        {name}
                      </h3>
                    </div>
                  </div>
                )}

                <div className="flex flex-1 flex-col p-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-emerald-200">
                      {type}
                    </span>
                    {featured && (
                      <span className="rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-amber-200">
                        Key Work
                      </span>
                    )}
                  </div>

                  <h3 className="mt-5 text-2xl font-extrabold text-white">
                    {name}
                  </h3>
                  <p className="mt-3 leading-7 text-zinc-400">{desc}</p>

                  <ul className="mt-5 space-y-3">
                    {impact.map((item) => (
                      <li
                        key={item}
                        className="flex gap-3 text-sm leading-6 text-zinc-300"
                      >
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-emerald-300" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {techStack.map((tech) => (
                      <span
                        key={tech}
                        className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm font-bold text-zinc-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto flex flex-wrap gap-3 pt-6">
                    {liveLink && (
                      <a
                        href={liveLink}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-md bg-emerald-400 px-4 py-2 text-sm font-extrabold text-[#080b0d] transition hover:bg-emerald-300"
                      >
                        Live Site
                      </a>
                    )}
                    {codeLink && (
                      <a
                        href={codeLink}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-md border border-white/15 px-4 py-2 text-sm font-bold text-zinc-200 transition hover:border-emerald-300 hover:text-emerald-200"
                      >
                        GitHub
                      </a>
                    )}
                  </div>
                </div>
              </article>
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
